import { useState, useEffect } from 'react';
import { FaUsers, FaPlus, FaTrophy, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import futbolistaService from '../../services/futbolistaService';
import torneoService from '../../services/torneoService';
import temporadaService from '../../services/temporadaService';
import TutorLayout from '../../components/tutor/TutorLayout';
import FutbolistaCard from '../../components/tutor/FutbolistaCard';

/**
 * Dashboard Principal del Tutor
 * 
 * Panel de control para padres/tutores donde pueden:
 * - Ver resumen de sus futbolistas
 * - Acceder a funciones principales
 * - Ver estadísticas básicas
 */
const TutorDashboard = () => {
  const navigate = useNavigate();
  const [futbolistas, setFutbolistas] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [torneosActivos, setTorneosActivos] = useState([]);
  const [temporadaActiva, setTemporadaActiva] = useState(null);
  const [todasTemporadas, setTodasTemporadas] = useState([]);
  const [selectedTemporadaId, setSelectedTemporadaId] = useState(null);

  useEffect(() => {
    loadFutbolistas();
    loadTemporadaActiva();
    loadTodasTemporadas();
  }, []);

  // Cargar torneos cuando la temporada activa esté disponible
  useEffect(() => {
    if (temporadaActiva) {
      loadTorneosActivos();
    }
  }, [temporadaActiva]);

  const loadFutbolistas = async () => {
    try {
      const futbolistasData = await futbolistaService.getFutbolistas();
      // Asegurar que siempre sea un array
      const futbolistasArray = Array.isArray(futbolistasData) ? futbolistasData : [];
      setFutbolistas(futbolistasArray);
    } catch (error) {
      // En caso de error, establecer array vacío
      setFutbolistas([]);
      showAlert('Error al cargar futbolistas', 'danger');
      console.error('Error:', error);
    }
  };

  const loadTemporadaActiva = async () => {
    try {
      const response = await temporadaService.getTemporadaActiva();

      if (response.success && response.data) {
        setTemporadaActiva(response.data);
        // Por defecto seleccionar la activa
        if (!selectedTemporadaId) {
          setSelectedTemporadaId(response.data.id);
        }
      } else {
        console.warn('No se pudo cargar temporada activa:', response.error);
        setTemporadaActiva(null);
      }
    } catch (error) {
      console.error('Error al cargar temporada activa:', error);
      setTemporadaActiva(null);
    }
  };

  const loadTodasTemporadas = async () => {
    try {
      const response = await temporadaService.getTemporadas();
      if (response.success && response.data) {
        // Ordenar: activa primero, luego por año descendente
        const sorted = [...response.data].sort((a, b) => {
          if (a.activa && !b.activa) return -1;
          if (!a.activa && b.activa) return 1;
          return (b.ano_inicio || 0) - (a.ano_inicio || 0);
        });
        setTodasTemporadas(sorted);
      }
    } catch (error) {
      console.error('Error al cargar temporadas:', error);
    }
  };

  const loadTorneosActivos = async () => {
    try {
      // Usar el servicio real de torneos para obtener solo torneos activos de la temporada actual
      const response = await torneoService.getTorneosActivos();

      if (response.success && response.data) {
        // Filtrar solo torneos de la temporada activa dinámicamente
        if (temporadaActiva) {
          const torneosTemporadaActual = response.data.filter(torneo =>
            torneo.temporada_nombre === temporadaActiva.nombre
          );
          setTorneosActivos(torneosTemporadaActual);
        } else {
          // Si no hay temporada activa cargada, mostrar todos los torneos activos
          setTorneosActivos(response.data);
        }
      } else {
        console.warn('No se pudieron cargar torneos activos:', response.error);
        setTorneosActivos([]);
      }
    } catch (error) {
      console.error('Error al cargar torneos:', error);
      setTorneosActivos([]);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  // Funciones auxiliares para FutbolistaCard
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 'N/A';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const getTemporadaActual = (futbolista) => {
    if (!futbolista.temporadas || futbolista.temporadas.length === 0) return null;

    // Si hay una temporada seleccionada, buscar esa
    if (selectedTemporadaId) {
      const tempSeleccionada = futbolista.temporadas.find(t => t.temporada?.id === selectedTemporadaId);
      if (tempSeleccionada) return tempSeleccionada;
    }

    // Fallback: buscar la temporada activa
    const temporadaActivaFut = futbolista.temporadas.find(t => t.temporada?.activa === true);
    if (temporadaActivaFut) return temporadaActivaFut;

    // Fallback: usar la temporada más reciente
    return futbolista.temporadas[futbolista.temporadas.length - 1];
  };

  // Manejar renovación de temporada
  const handleRenovar = async (futbolista) => {
    const ultimaTemp = futbolista.temporadas?.sort((a, b) => b.temporada_id - a.temporada_id)[0];
    const clubNombre = ultimaTemp?.club?.nombre || 'Sin club';
    const divisionNombre = ultimaTemp?.division ? `${ultimaTemp.division.competicion} (${ultimaTemp.division.federacion})` : 'Sin división';

    const confirmar = window.confirm(
      `🔄 Renovar a ${futbolista.nombre} para la temporada ${temporadaActiva?.nombre}\n\n` +
      `Se copiarán los datos de la temporada anterior:\n` +
      `• Club: ${clubNombre}\n` +
      `• División: ${divisionNombre}\n\n` +
      `Los ProPoints empezarán de 0 en la nueva temporada.\n\n` +
      `¿Confirmar renovación?`
    );

    if (!confirmar) return;

    try {
      await futbolistaService.renovarTemporada(futbolista.id);
      setAlert({ show: true, message: `✅ ${futbolista.nombre} renovado para ${temporadaActiva?.nombre}`, variant: 'success' });
      // Recargar futbolistas
      const data = await futbolistaService.getFutbolistas();
      setFutbolistas(data);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al renovar temporada';
      setAlert({ show: true, message: `❌ ${msg}`, variant: 'danger' });
    }
  };

  // Calcular estadísticas con validaciones defensivas
  const futbolistasArray = Array.isArray(futbolistas) ? futbolistas : [];
  const torneosActivosArray = Array.isArray(torneosActivos) ? torneosActivos : [];

  // Filtrar futbolistas por temporada seleccionada
  const selectedTemporada = todasTemporadas.find(t => t.id === selectedTemporadaId);
  const esTemporadaActiva = selectedTemporada?.activa || false;
  const futbolistasFiltrados = selectedTemporadaId
    ? futbolistasArray.filter(f =>
        f.temporadas && f.temporadas.some(t => t.temporada?.id === selectedTemporadaId)
      )
    : futbolistasArray;

  const stats = {
    totalFutbolistas: futbolistasArray.length,
    futbolistasActivos: futbolistasArray.filter(f => f.temporadas && f.temporadas.length > 0).length,
    temporadaActual: futbolistasFiltrados.length,
    torneosActivos: torneosActivosArray.length,
    futbolistasEnTorneos: futbolistasArray.filter(f =>
      f.inscripciones && f.inscripciones.some(inscripcion => inscripcion.activa === true)
    ).length
  };

  return (
    <TutorLayout title="Panel del Tutor" subtitle="Gestiona a tus futbolistas y su participación en ProKids">
      {alert.show && (
        <div className={`alert alert-${alert.variant === 'danger' ? 'danger' : alert.variant === 'success' ? 'success' : 'info'}`} style={{ marginBottom: '20px' }}>
          {alert.message}
        </div>
      )}

      {/* Estadísticas principales */}
      <div className="stats-grid" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div className="stat-icon">
            <FaUsers size={32} />
          </div>
          <div className="stat-content">
            <h3>{stats.totalFutbolistas}</h3>
            <p>Futbolistas Registrados</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ color: 'var(--success-color)' }}>
            <FaCalendarAlt size={32} />
          </div>
          <div className="stat-content">
            <h3>{stats.temporadaActual}</h3>
            <p>En Temporada {selectedTemporada?.nombre || temporadaActiva?.nombre || 'N/A'}</p>
          </div>
        </div>
        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
          border: '2px solid #28a745',
          boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
        }}>
          <div className="stat-icon" style={{ color: '#FFFFFF' }}>
            <FaTrophy size={32} />
          </div>
          <div className="stat-content">
            <h3 style={{ color: '#FFFFFF', fontWeight: '700' }}>{stats.torneosActivos}</h3>
            <p style={{ color: '#FFFFFF', fontWeight: '600' }}>Torneos Activos</p>
            <small style={{ color: '#FFFFFF', opacity: 0.9, fontSize: '0.8rem' }}>
              {stats.futbolistasEnTorneos} futbolistas participando
            </small>
          </div>
        </div>
        <div className="stat-card">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/tutor/futbolistas/nuevo')}
            style={{ width: '100%', height: '100%', minHeight: '80px' }}
          >
            <FaPlus style={{ marginRight: '8px' }} />
            Nuevo Futbolista
          </button>
        </div>
      </div>

      {/* Accesos rápidos */}
      <div className="admin-card">
        <div className="card-header">
          <h3>Accesos Rápidos</h3>
        </div>
        <div className="card-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate('/tutor/futbolistas')}
            >
              <FaUsers style={{ marginRight: '8px' }} />
              Gestionar Futbolistas
            </button>
            {/* Solo mostrar si tiene futbolistas */}
            {futbolistasArray.length > 0 && (
              <>
                <button
                  className="btn btn-outline-success"
                  onClick={() => navigate('/products')}
                >
                  <FaTrophy style={{ marginRight: '8px' }} />
                  Comprar Jornadas
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/matches')}
                >
                  <FaCalendarAlt style={{ marginRight: '8px' }} />
                  Ver Torneos y Jornadas
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Lista de futbolistas */}
      <div className="admin-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h3>Mis Futbolistas</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {todasTemporadas.length > 1 && (
              <select
                value={selectedTemporadaId || ''}
                onChange={(e) => setSelectedTemporadaId(parseInt(e.target.value))}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '2px solid #E8ECFF',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#061156',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {todasTemporadas.map(temp => (
                  <option key={temp.id} value={temp.id}>
                    📅 {temp.nombre} {temp.activa ? '(Actual)' : ''}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate('/tutor/futbolistas')}
            >
              Ver Todos
            </button>
          </div>
        </div>
        <div className="card-content">
          {!esTemporadaActiva && selectedTemporada && (
            <div style={{
              backgroundColor: '#FFF3E0', color: '#E65100', padding: '10px 16px',
              borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '15px'
            }}>
              ⚠️ Viendo temporada <strong>{selectedTemporada.nombre}</strong> (histórico)
            </div>
          )}
          {futbolistasArray.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <FaUsers style={{ color: 'var(--text-secondary)', marginBottom: '16px' }} size={60} />
              <h4 style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>No tienes futbolistas registrados</h4>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>Crea tu primer futbolista para comenzar</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/tutor/futbolistas/nuevo')}
              >
                <FaPlus style={{ marginRight: '8px' }} />
                Crear Primer Futbolista
              </button>
            </div>
          ) : futbolistasFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-secondary)' }}>
              <p>Ningún futbolista participó en la temporada <strong>{selectedTemporada?.nombre}</strong></p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              gap: '20px',
              padding: '20px 0'
            }}>
              {futbolistasFiltrados.map(futbolista => (
                <FutbolistaCard
                  key={futbolista.id}
                  futbolista={futbolista}
                  calcularEdad={calcularEdad}
                  getTemporadaActual={getTemporadaActual}
                  temporadaActivaSistema={temporadaActiva}
                  onRenovar={handleRenovar}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </TutorLayout>
  );
};

export default TutorDashboard;
